<template>
  <component
    :is="variant === 'card' ? VCard : 'div'"
    v-bind="variant === 'card' ? { variant: 'flat', border: true } : {}"
    :class="{ 'custom-form-field--plain': variant !== 'card' }">
    <editable-key-value
      :class="{ 'pa-4': variant === 'card' }"
      :can-edit="isEditable && Boolean(formField.field?.id)"
      :is-editing="isEditing"
      :always-show-edit-button="alwaysShowEditButton"
      :save-button-props="{ disabled: !canSave }"
      @edit="isEditing = true"
      @cancel="handleCancel"
      @save="handleSave">
      <template #label>
        {{ formField.label }}
        <span v-if="formField.required" class="text-red"><strong>*</strong></span>
      </template>
      <template #value>
        <span v-html="formField.formattedValue"></span>
      </template>
      <template #editing-value>
        <v-form ref="form">
          <component
            :is="getComponent(formField.field.type)"
            v-bind="getComponentProps(formField.field.type)"
            v-model="localValue"
            :field="customField" />
        </v-form>
      </template>
    </editable-key-value>
  </component>
</template>
<script>
import { CustomFieldType, isValidCustomField, json, sanitizeCustomFieldValue } from '@nova/core';
import { isString } from 'class-validator';
import { ref, defineComponent, computed, watch } from 'vue';
import { VCard } from 'vuetify/components';

import { EditableKeyValue } from '@satellite/components/elements/key_value';

export default defineComponent({
  name: 'CustomFormsMultipleTriggerFormField',
  components: {
    EditableKeyValue
  },
  props: {
    /*
     * FormField entity instace from custom forms
     * An extra property called dataId is the customformdata.id
     * Used to update the existing data, when not present,
     * just create a new instance of custom form data
     */
    formField: {
      type: Object,
      required: true
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'plain',
      validator(value) {
        return ['card', 'plain'].includes(value);
      }
    },
    componentsMapping: {
      type: Object,
      required: false,
      default: () => ({})
    },
    timezone: {
      type: String,
      required: false
    },
    militaryTimeEnabled: {
      type: Boolean,
      required: false
    },
    alwaysShowEditButton: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:formField'],
  setup(props, { emit }) {
    const isLoading = ref(false);
    const isEditing = ref(false);
    const localValue = ref(null);

    /**
     * Normalizes a stored value to the format expected by isValidCustomField and the API.
     *
     * DropDownMultiSelect / MultiDocument: CustomFormData stores the array as a JSON string.
     * Parse it back so array validators pass and the API receives the correct type.
     */
    const normalizeValueForType = (type, value) => {
      if (
        (type === CustomFieldType.DropDownMultiSelect || type === CustomFieldType.MultiDocument) &&
        isString(value)
      ) {
        const parsed = json.safeParse(value);
        if (Array.isArray(parsed)) {
          return sanitizeCustomFieldValue(type, parsed);
        }
      }
      if (type === CustomFieldType.DropDownMultiSelect) {
        return sanitizeCustomFieldValue(type, value);
      }
      return value;
    };

    // This is a mapping to the custom field components
    const customField = computed(() => {
      return {
        name: props.formField.id,
        order: props.formField.order,
        value: props.formField.value,
        required: props.formField.required,
        placeholder: props.formField.field.placeholder,
        type: props.formField.field.type,
        dropDownValues: props.formField.field.extraFields?.dropDownValues,
        minLengthOrValue: props.formField.field.extraFields?.minLengthOrValue,
        maxLengthOrValue: props.formField.field.extraFields?.maxLengthOrValue
      };
    });

    const canSave = computed(() => {
      let isFilled = true;

      const normalizedValue = normalizeValueForType(customField.value.type, localValue.value);
      const field = { ...customField.value, value: normalizedValue };

      if (field.type !== CustomFieldType.Bool) {
        if (field.required && !field.value) {
          isFilled = false;
        }
      }
      return isFilled && isValidCustomField(field);
    });

    const getComponent = type => {
      return props.componentsMapping[type]?.component ?? 'v-text-field';
    };

    const getComponentProps = type => {
      // Shallow-clone to avoid mutating the original componentsMapping props object across renders
      const componentProps = { ...(props.componentsMapping[type]?.props || {}) };

      componentProps.testId = `custom-form-field-${props.formField.id}`;
      componentProps.timezone = props.timezone;
      componentProps.militaryTimeEnabled = props.militaryTimeEnabled;
      componentProps.required = customField.value.required ?? false;
      componentProps.placeholder = customField.value.placeholder;

      // This is meant for Luna which has a different component for the custom fields
      if (getComponent(type)?.name === 'BasicFormField') {
        componentProps.fieldName = props.formField.id;
        componentProps.hint = customField.value.description;

        // Merge into existing fieldProps rather than replacing so any type-specific props
        // defined in the mapping (e.g. { type: 'text' }) are preserved.
        componentProps.fieldProps = {
          ...(componentProps.fieldProps || {}),
          timezone: props.timezone,
          militaryTimeEnabled: props.militaryTimeEnabled,
          // DropDownField needs multiple=true to emit arrays for multiselect fields
          ...(type === CustomFieldType.DropDownMultiSelect && { multiple: true }),
          // DocumentUpload needs isMultiple=true so it allows uploading several files
          ...(type === CustomFieldType.MultiDocument && { isMultiple: true }),
          // Pass the current value so DocumentUpload initializes with existing files
          // rendered, making them visible and removable.
          // Luna's DocumentUpload emits string[] even for a single-file field, so
          // normalise Document values from array → string here (app-specific behaviour).
          ...((type === CustomFieldType.Document || type === CustomFieldType.MultiDocument) && {
            value: (() => {
              const v = normalizeValueForType(type, localValue.value);
              return type === CustomFieldType.Document && Array.isArray(v) ? (v[0] ?? null) : v;
            })()
          })
        };

        if (customField.value.dropDownValues) {
          componentProps.options = [...customField.value.dropDownValues];
        }
      }

      return componentProps;
    };

    const handleSave = async () => {
      isLoading.value = true;
      try {
        const formData = {
          id: props.formField.dataId,
          formFieldId: props.formField.id,
          objectId: props.formField.objectId,
          triggerId: props.formField.triggerId,
          entityName: props.formField.entityName ?? 'appointment',
          warehouseId: props.formField.warehouseId,
          value: normalizeValueForType(customField.value.type, localValue.value)
        };

        if (formData.id) {
          await window.axios.patch(`custom-forms/form-data/${formData.id}`, {
            value: formData.value
          });
        } else {
          await window.axios.post(`custom-forms/form-data`, formData);
        }

        isEditing.value = false;
        emit('update:formField', { ...formData, label: props.formField.label });
      } catch (error) {
        console.error('Error saving custom field:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const handleCancel = () => {
      isEditing.value = false;
      localValue.value = normalizeValueForType(props.formField.field?.type, props.formField.value);
    };

    watch(
      () => props.formField.value,
      newValue => {
        localValue.value = normalizeValueForType(props.formField.field?.type, newValue);
      },
      { immediate: true, deep: true }
    );

    return {
      VCard,
      isLoading,
      isEditing,
      handleCancel,
      handleSave,
      customField,
      getComponent,
      getComponentProps,
      localValue,
      canSave
    };
  }
});
</script>

<style scoped>
.custom-form-field--plain {
  display: contents;
}
</style>
